/**
 * SAPPT Design Tokens — Partner Frontend (Amber brand)
 *
 * 단일 진실 공급원. admin frontend/src/theme/tokens.ts 와 키 구조가 동일하며
 * brand 색상만 Amber 계열로 차별화됩니다. 4px 그리드 준수.
 */

// ──────────────────────────────────────────────
// Brand (Amber) — Partner 전용
// ──────────────────────────────────────────────
export const brand = {
  primary: '#f59e0b',
  primaryHover: '#fbbf24',
  primaryActive: '#d97706',
  primarySoft: '#fcd34d',
  primarySoftBg: '#fffbeb',
  primaryGradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
  primaryShadow: '0 2px 8px rgba(245,158,11,0.25)',
  primaryRing: 'rgba(245,158,11,0.08)',
} as const;

// ──────────────────────────────────────────────
// Gray Scale (slate) — admin과 동일
// ──────────────────────────────────────────────
export const gray = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
} as const;

// ──────────────────────────────────────────────
// State Colors — admin과 동일
// ──────────────────────────────────────────────
export const state = {
  success: '#10b981',
  successSoft: '#dcfce7',
  successText: '#15803d',
  warning: '#f97316',
  warningSoft: '#fef3c7',
  warningText: '#9a3412',
  error: '#ef4444',
  errorSoft: '#fee2e2',
  errorText: '#b91c1c',
  info: '#0ea5e9',
  infoSoft: '#e0f2fe',
  infoText: '#0369a1',
} as const;

// ──────────────────────────────────────────────
// Spacing — 4px Grid
// ──────────────────────────────────────────────
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
} as const;

// ──────────────────────────────────────────────
// Border Radius
// ──────────────────────────────────────────────
export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  full: 9999,
} as const;

// ──────────────────────────────────────────────
// Shadows
// ──────────────────────────────────────────────
export const shadow = {
  sm: '0 1px 2px rgba(0,0,0,0.04)',
  md: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.03)',
  lg: '0 4px 16px rgba(0,0,0,0.08)',
  xl: '0 8px 28px rgba(0,0,0,0.12)',
  brand: '0 4px 20px rgba(245,158,11,0.30)',
} as const;

// ──────────────────────────────────────────────
// Typography
// ──────────────────────────────────────────────
export const font = {
  /* lab 과 동일 스택 — 한글 폴백(Apple SD Gothic Neo·Malgun Gothic) 포함 */
  family: "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, "
        + "'Apple SD Gothic Neo', 'Segoe UI', Roboto, 'Malgun Gothic', sans-serif",
  mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'D2Coding', monospace",
  size: {
    xs: 11,
    sm: 12,
    base: 14,
    md: 15,
    lg: 16,
    xl: 18,
    '2xl': 22,
    '3xl': 28,
  },
  weight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.4,
    base: 1.6,
    relaxed: 1.8,
  },
} as const;

// ──────────────────────────────────────────────
// Semantic aliases
// ──────────────────────────────────────────────
export const color = {
  bg: '#ffffff',
  bgLayout: brand.primarySoftBg,
  bgSubtle: gray[50],
  bgElevated: '#ffffff',
  border: '#fde68a',
  borderSubtle: '#fef3c7',
  text: gray[800],
  textSecondary: gray[500],
  textTertiary: gray[400],
  textOnPrimary: '#ffffff',
} as const;

// ──────────────────────────────────────────────
// 레이아웃 상수 — lab 사본. 한쪽만 고치지 말 것
// ──────────────────────────────────────────────
export const layout = {
  headerHeight: 44,
  sidebarWidth: 208,
  sidebarCollapsed: 48,
  /* MainLayout Content 패딩. useViewportFit 이 하단 예약값으로 같은 값을 읽는다 —
     둘이 어긋나면 표 높이 실측이 틀어져 문서 세로스크롤이 생긴다 (2026-09-03 밀도 키트) */
  contentPadding: 8,
} as const;

// ──────────────────────────────────────────────
// 밀도 키트 v3 (2026-09-03) — '장비 콘솔' 밀도
// lab frontend/src/theme/tokens.ts 의 사본. 값이 갈라지면 두 포털의 밀도가 어긋난다
// (2026-09-03 실측 시점에 사이드바 폭 230/240, 메뉴 항목 38/40 으로 이미 갈라져 있었다).
//
// 함정: 표의 가로 패딩은 8을 상한으로 둔다 — 넓히면 열이 좁아져 줄바꿈으로 행이 되레 커진다.
// ──────────────────────────────────────────────
export const density = {
  fontSize: 13,
  fontSizeSmall: 12,
  lineHeight: 1.45,
  controlHeight: 28,
  controlHeightSM: 22,

  menuItemHeight: 30,
  menuItemMarginBlock: 1,
  menuItemMarginInline: 6,
  menuItemPaddingInline: 12,
  menuIconSize: 14,

  cardHeaderHeight: 32,
  cardHeaderPadding: 10,
  cardBodyPadding: 10,

  tableCellPaddingBlock: 4,
  tableCellPaddingInline: 8,   // 상한 — 넘기지 말 것

  formItemMarginBottom: 10,
  formLabelHeight: 20,

  blockGap: 8,
} as const;

export const tokens = { brand, gray, state, space, radius, shadow, font, color, layout, density } as const;
export type Tokens = typeof tokens;
